/**
 * Escena animada de la estación de servicio (panel de acceso).
 *
 * Es un corte de la estación dibujado a mano en SVG con la paleta del panel y
 * animado **sólo con `@keyframes` de CSS** (sin librerías): la misma técnica del
 * panel de presentación que el usuario tomó de referencia.
 *
 * La escena **no pinta su propio cielo**: deja ver el degradado turquesa del
 * panel y sólo añade un *velo* (transparente arriba, oscuro abajo) para que la
 * parte técnica gane contraste sin que se note dónde empieza el dibujo. Los
 * cerros se funden con degradados y el único plano macizo es el corte de tierra.
 *
 * El relato de la presentación (una sola pasada, ~3 s) es:
 *   1. cerros y suelo entran deslizándose (parallax),
 *   2. aparece el techo, después los surtidores y el letrero de precios,
 *   3. se revela el corte subterráneo: tanques, sondas y el cableado al
 *      controlador **NEXUS**.
 *
 * Después del arranque quedan sólo bucles suaves (niveles, pulsos de telemetría,
 * lámparas y precios rotando), que es lo que mantiene viva la pantalla sin
 * distraer del formulario. Todo se apaga con `prefers-reduced-motion`
 * (`src/index.css`).
 *
 * Elementos: **techo**, **dos surtidores**, **letrero de precios** (REGULAR,
 * PREMIUM y DIESEL) y las **sondas de telemedición de los tanques** (varilla,
 * flotador y cable hasta el controlador).
 */

import { useId } from 'react';

export function EscenaEstacion() {
  const id = useId().replace(/[^a-zA-Z0-9]/g, '');
  const velo = `velo-${id}`;
  const suelo = `suelo-${id}`;
  const luzTecho = `luz-techo-${id}`;
  const gas90 = `gas-90-${id}`;
  const diesel = `diesel-${id}`;
  const cerroLejos = `cerro-lejos-${id}`;
  const cerroCerca = `cerro-cerca-${id}`;
  const corteA = `corte-a-${id}`;
  const corteB = `corte-b-${id}`;
  const tierra = `tierra-${id}`;

  return (
    <svg
      viewBox="0 0 1200 640"
      preserveAspectRatio="xMidYMax slice"
      className="h-full w-full font-sans"
      role="img"
      aria-label="Estación de servicio EDSuite: techo, surtidores, letrero de precios de REGULAR, PREMIUM y DIESEL, y los tanques con sus sondas de telemedición conectadas al controlador NEXUS"
    >
      <defs>
        <linearGradient id={velo} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#08141c" stopOpacity="0" />
          <stop offset="38%" stopColor="#08141c" stopOpacity="0.3" />
          <stop offset="62%" stopColor="#08141c" stopOpacity="0.72" />
          <stop offset="100%" stopColor="#08141c" stopOpacity="0.94" />
        </linearGradient>
        <linearGradient id={cerroLejos} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0b1a24" stopOpacity="0" />
          <stop offset="100%" stopColor="#0b1a24" stopOpacity="0.75" />
        </linearGradient>
        <linearGradient id={cerroCerca} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#071119" stopOpacity="0" />
          <stop offset="100%" stopColor="#071119" stopOpacity="0.9" />
        </linearGradient>
        <linearGradient id={luzTecho} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={gas90} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.28" />
        </linearGradient>
        <linearGradient id={diesel} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#0891b2" stopOpacity="0.25" />
        </linearGradient>
        <linearGradient id={suelo} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0f2430" />
          <stop offset="100%" stopColor="#08121a" />
        </linearGradient>
        <pattern id={tierra} width="18" height="18" patternUnits="userSpaceOnUse">
          <path d="M0 18 L18 0" stroke="#1e3a4c" strokeWidth="1" opacity="0.5" />
        </pattern>
        <clipPath id={corteA}>
          <rect x="390" y="470" width="230" height="96" rx="18" />
        </clipPath>
        <clipPath id={corteB}>
          <rect x="700" y="470" width="230" height="96" rx="18" />
        </clipPath>
      </defs>

      {/* Velo: oscurece el fondo del panel sin bordes duros */}
      <rect x="0" y="0" width="1200" height="640" fill={`url(#${velo})`} />

      {/* ---------- Cerros (parallax), fundidos con el degradado ---------- */}
      <g className="esc-cerros-lejos">
        <path
          d="M-40 402 L110 300 L190 352 L300 262 L410 348 L500 296 L610 356 L700 300 L820 400 L900 402 Z"
          fill={`url(#${cerroLejos})`}
          stroke="#22d3ee"
          strokeOpacity="0.25"
          strokeWidth="1.5"
        />
      </g>
      <g className="esc-cerros-cerca">
        <path
          d="M-40 402 L120 338 L250 386 L390 318 L540 378 L660 324 L800 384 L940 330 L1080 390 L1240 402 Z"
          fill={`url(#${cerroCerca})`}
          stroke="#22d3ee"
          strokeOpacity="0.3"
          strokeWidth="1.5"
        />
      </g>

      {/* ---------- Suelo y corte subterráneo ---------- */}
      <rect x="-20" y="402" width="1240" height="238" fill={`url(#${suelo})`} />
      <rect x="-20" y="402" width="1240" height="238" fill={`url(#${tierra})`} opacity="0.35" />
      <line x1="-20" y1="470" x2="1220" y2="470" stroke="#1e3a4c" strokeWidth="1" strokeDasharray="12 10" />
      <line x1="-20" y1="560" x2="1220" y2="560" stroke="#1e3a4c" strokeWidth="1" strokeDasharray="12 10" />

      {/* ---------- Vía frente a la estación ---------- */}
      <rect x="-20" y="394" width="1240" height="10" fill="#0d1f2a" />
      <line
        x1="-20"
        y1="399"
        x2="1220"
        y2="399"
        stroke="#e2e8f0"
        strokeOpacity="0.18"
        strokeWidth="2"
        strokeDasharray="26 30"
      />

      {/* ---------- Tanques con sondas de telemedición ---------- */}
      {[
        {
          x: 390,
          corte: corteA,
          centro: 505,
          liquido: gas90,
          /* Altura del líquido = porcentaje rotulado (62 % de 88). */
          altura: 55,
          etiqueta: 'TANQUE 1 · REGULAR',
          porcentaje: '62',
        },
        {
          x: 700,
          corte: corteB,
          centro: 815,
          liquido: diesel,
          altura: 36,
          etiqueta: 'TANQUE 2 · DIÉSEL',
          porcentaje: '41',
        },
      ].map((tanque, indice) => (
        <g key={tanque.centro} className={`esc-tanque esc-tanque--${indice + 1}`}>
          {/* Cuerpo del tanque y líquido (el nivel oscila dentro del corte) */}
          <rect
            x={tanque.x}
            y="470"
            width="230"
            height="96"
            rx="18"
            fill="#132c39"
            stroke="#22d3ee"
            strokeOpacity="0.35"
            strokeWidth="1.5"
          />
          <g clipPath={`url(#${tanque.corte})`}>
            <rect
              className="esc-nivel"
              x={tanque.x + 4}
              y={562 - tanque.altura}
              width="222"
              height={tanque.altura}
              fill={`url(#${tanque.liquido})`}
            />
          </g>

          {/* Varilla de la sonda + flotador de medición */}
          <rect
            x={tanque.centro - 3}
            y="448"
            width="6"
            height="112"
            rx="3"
            fill="#e2e8f0"
            fillOpacity="0.12"
            stroke="#a5f3fc"
            strokeOpacity="0.5"
          />
          <rect
            className="esc-flotador"
            x={tanque.centro - 15}
            y="502"
            width="30"
            height="9"
            rx="4.5"
            fill="#a5f3fc"
            fillOpacity="0.75"
          />

          {/* Transmisor de la sonda, en la superficie */}
          <rect
            x={tanque.centro - 9}
            y="434"
            width="18"
            height="14"
            rx="4"
            fill="#0e7490"
            fillOpacity="0.85"
            stroke="#a5f3fc"
            strokeOpacity="0.7"
          />
          <ellipse
            className="esc-tapa"
            cx={tanque.centro}
            cy="399"
            rx="17"
            ry="4.5"
            fill="#0d1f2a"
            stroke="#67e8f9"
            strokeOpacity="0.5"
          />
          <line
            x1={tanque.centro}
            y1="403"
            x2={tanque.centro}
            y2="434"
            stroke="#a5f3fc"
            strokeOpacity="0.35"
            strokeWidth="1.5"
            strokeDasharray="5 5"
          />

          {/* Cableado enterrado hasta el controlador */}
          <path
            className={`esc-cable esc-cable--${indice + 1}`}
            d={`M${tanque.centro} 448 L${tanque.centro} ${indice === 0 ? 420 : 428} L${
              indice === 0 ? 330 : 360
            } ${indice === 0 ? 420 : 428} L${indice === 0 ? 330 : 360} 392`}
            fill="none"
            stroke="#67e8f9"
            strokeOpacity="0.45"
            strokeWidth="1.5"
            strokeDasharray="7 5"
          />
          {[0, 1].map((pulso) => (
            <circle
              key={pulso}
              className={`esc-pulso esc-pulso--${indice + 1}`}
              cx={tanque.centro}
              cy="446"
              r="2.6"
              fill="#cffafe"
              style={{ animationDelay: `${indice * 0.6 + pulso * 1.6}s` }}
            />
          ))}

          {/* Rótulos del tanque */}
          <text
            x={tanque.centro}
            y="594"
            textAnchor="middle"
            fontSize="11"
            letterSpacing="1.4"
            fill="#a5f3fc"
            fillOpacity="0.85"
          >
            {tanque.etiqueta}
          </text>
          <text
            x={tanque.centro}
            y="612"
            textAnchor="middle"
            fontSize="10"
            letterSpacing="0.6"
            fill="#e2e8f0"
            fillOpacity="0.5"
          >
            sonda de telemedición · {tanque.porcentaje} %
          </text>
        </g>
      ))}

      {/* ---------- Controlador de tanques ---------- */}
      <g className="esc-gabinete">
        <rect
          x="296"
          y="340"
          width="50"
          height="52"
          rx="5"
          fill="#132c39"
          stroke="#22d3ee"
          strokeOpacity="0.5"
        />
        <rect x="304" y="347" width="34" height="15" rx="2" fill="#0b1a24" stroke="#22d3ee" strokeOpacity="0.55" />
        <text x="321" y="378" textAnchor="middle" fontSize="8" letterSpacing="1" fill="#a5f3fc" fillOpacity="0.9">
          NEXUS
        </text>
        <circle className="esc-led" cx="321" cy="386" r="2.6" fill="#34d399" />
      </g>

      {/* ---------- Letrero de precios ---------- */}
      <g className="esc-letrero">
        <rect x="176" y="252" width="12" height="150" rx="3" fill="#132c39" stroke="#22d3ee" strokeOpacity="0.4" />
        <rect
          x="96"
          y="134"
          width="178"
          height="122"
          rx="10"
          fill="#0b1a24"
          stroke="#22d3ee"
          strokeOpacity="0.55"
          strokeWidth="1.5"
        />
        <rect x="96" y="134" width="178" height="24" rx="10" fill="#0891b2" fillOpacity="0.45" />
        <text x="185" y="150" textAnchor="middle" fontSize="10" letterSpacing="2.4" fill="#cffafe">
          PRECIOS
        </text>
        {[
          { y: 164, producto: 'REGULAR', precio: '16.90' },
          { y: 194, producto: 'PREMIUM', precio: '17.80' },
          { y: 224, producto: 'DIESEL', precio: '18.40' },
        ].map((fila, indice) => (
          <g key={fila.producto}>
            <rect
              className={`esc-precio esc-precio--${indice + 1}`}
              x="102"
              y={fila.y}
              width="166"
              height="26"
              rx="5"
              fill="#22d3ee"
              fillOpacity="0.1"
            />
            <text x="110" y={fila.y + 17} fontSize="9" fill="#cffafe" fillOpacity="0.9" letterSpacing="0.6">
              {fila.producto}
            </text>
            <text
              x="262"
              y={fila.y + 17}
              textAnchor="end"
              fontSize="12"
              fontFamily="ui-monospace, SFMono-Regular, monospace"
              fill="#e2e8f0"
            >
              {fila.precio}
            </text>
          </g>
        ))}
        <rect x="168" y="392" width="28" height="10" rx="3" fill="#132c39" stroke="#22d3ee" strokeOpacity="0.35" />
      </g>
      {/* ---------- Techo (canopy) ---------- */}
      <g className="esc-techo">
        <polygon points="360,204 880,204 986,402 254,402" fill={`url(#${luzTecho})`} />
        <rect
          x="330"
          y="166"
          width="580"
          height="18"
          rx="9"
          fill="#132c39"
          stroke="#22d3ee"
          strokeOpacity="0.5"
          strokeWidth="1.5"
        />
        <rect x="330" y="184" width="580" height="20" fill="#0b1a24" />
        <text x="620" y="198" textAnchor="middle" fontSize="10" letterSpacing="4" fill="#a5f3fc" fillOpacity="0.85">
          EDSUITE
        </text>
        <rect x="366" y="204" width="13" height="198" rx="3" fill="#132c39" stroke="#22d3ee" strokeOpacity="0.3" />
        <rect x="861" y="204" width="13" height="198" rx="3" fill="#132c39" stroke="#22d3ee" strokeOpacity="0.3" />
        {[430, 575, 720].map((x, indice) => (
          <rect
            key={x}
            className={`esc-lampara esc-lampara--${indice + 1}`}
            x={x}
            y="204"
            width="90"
            height="5"
            rx="2.5"
            fill="#a5f3fc"
          />
        ))}
      </g>

      {/* ---------- Surtidores ---------- */}
      {[
        { x: 470, banda: '#fbbf24', producto: 'REGULAR' },
        { x: 700, banda: '#22d3ee', producto: 'DIESEL' },
      ].map((surtidor, indice) => (
        <g key={surtidor.x} className={`esc-surtidor esc-surtidor--${indice + 1}`}>
          <rect x={surtidor.x - 8} y="394" width="80" height="8" rx="3" fill="#132c39" />
          <rect
            x={surtidor.x}
            y="264"
            width="64"
            height="134"
            rx="9"
            fill="#132c39"
            stroke="#22d3ee"
            strokeOpacity="0.45"
            strokeWidth="1.5"
          />
          <rect
            x={surtidor.x + 10}
            y="276"
            width="44"
            height="26"
            rx="4"
            fill="#0b1a24"
            stroke="#22d3ee"
            strokeOpacity="0.6"
          />
          <rect x={surtidor.x + 17} y="282" width="30" height="3" rx="1.5" fill="#67e8f9" fillOpacity="0.7" />
          <rect x={surtidor.x + 17} y="290" width="22" height="3" rx="1.5" fill="#67e8f9" fillOpacity="0.4" />
          <rect x={surtidor.x} y="308" width="64" height="7" fill={surtidor.banda} fillOpacity="0.55" />
          <text
            x={surtidor.x + 32}
            y="303"
            textAnchor="middle"
            fontSize="7.5"
            letterSpacing="0.6"
            fill="#cffafe"
            fillOpacity="0.85"
          >
            {surtidor.producto}
          </text>
          <path
            d={`M${surtidor.x + 2} 274 C ${surtidor.x - 22} 282 ${surtidor.x - 20} 308 ${surtidor.x - 11} 334`}
            fill="none"
            stroke="#a5f3fc"
            strokeOpacity="0.4"
            strokeWidth="2"
          />
          <path
            d={`M${surtidor.x + 62} 274 C ${surtidor.x + 86} 282 ${surtidor.x + 84} 308 ${surtidor.x + 75} 334`}
            fill="none"
            stroke="#a5f3fc"
            strokeOpacity="0.4"
            strokeWidth="2"
          />
          <rect
            x={surtidor.x - 13}
            y="332"
            width="12"
            height="24"
            rx="3"
            fill="#0e7490"
            fillOpacity="0.85"
            stroke="#a5f3fc"
            strokeOpacity="0.55"
          />
          <rect
            x={surtidor.x + 65}
            y="332"
            width="12"
            height="24"
            rx="3"
            fill="#0e7490"
            fillOpacity="0.85"
            stroke="#a5f3fc"
            strokeOpacity="0.55"
          />
        </g>
      ))}

      {/* ---------- Primer plano ---------- */}
      <g className="esc-primer-plano">
        {[
          'M404 396 L416 378 L432 392 L446 380 L458 396 Z',
          'M806 396 L822 374 L840 392 L856 376 L872 396 Z',
        ].map((d) => (
          <path key={d} d={d} fill="#0a161e" stroke="#22d3ee" strokeOpacity="0.2" />
        ))}
      </g>
    </svg>
  );
}
